import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp,
  X,
  AlertTriangle,
  Activity,
  ShieldAlert,
  PowerOff,
  RefreshCw,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
} from 'lucide-react';
import type {
  DecimalString,
  TraderPosition,
  TraderSnapshot,
  TraderCommandResult,
  BridgeHealthStatus,
} from './types/trader-protocol.js';

export interface FloatingTraderModalProps {
  entry: {
    moduleId: string;
    moduleName: string;
    version: string;
    slot: string;
    label?: string;
  };
  onClose?: () => void;
}

export const FloatingTraderModal: React.FC<FloatingTraderModalProps> = ({ entry, onClose }) => {
  const [snapshot, setSnapshot] = useState<TraderSnapshot>({
    bridgeStatus: 'connected',
    accountId: 'MT5-DEMO-882194',
    broker: 'MetaQuotes-Demo',
    currency: 'USD',
    balance: '50000.00',
    equity: '50420.50',
    marginUsed: '1450.00',
    freeMargin: '48970.50',
    positions: [],
    serverTime: new Date().toISOString(),
    sequenceId: 1,
  });

  const [connectionStatus, setConnectionStatus] = useState<BridgeHealthStatus | 'reconnecting'>('connected');
  const [logs, setLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] Bridge initialized. Subscribed to channel trader:${snapshot.accountId}`,
  ]);
  const [rejectedIntentModal, setRejectedIntentModal] = useState<TraderCommandResult | null>(null);
  const [equityHistory, setEquityHistory] = useState<number[]>([50000, 50120, 50250, 50180, 50350, 50420]);
  const [isEmergencyDisconnected, setIsEmergencyDisconnected] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // 1. Initial snapshot fetch
  useEffect(() => {
    if (isEmergencyDisconnected) return;

    fetch('/api/v1/trader/snapshot')
      .then((res) => res.json())
      .then((res) => {
        if (res.ok && res.data) {
          setSnapshot(res.data);
          setConnectionStatus(res.data.bridgeStatus);
          setEquityHistory((prev) => [...prev.slice(-19), parseFloat(res.data.equity)]);
          addLog(`[SNAPSHOT] Loaded seq #${res.data.sequenceId} (${res.data.positions.length} open positions)`);
        }
      })
      .catch(() => {
        setConnectionStatus('reconnecting');
        addLog('[WARN] Snapshot fetch failed. Retrying over WebSocket...');
      });
  }, [isEmergencyDisconnected]);

  // 2. Real-time WebSocket telemetry loop
  useEffect(() => {
    if (isEmergencyDisconnected) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    let ws: WebSocket;

    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionStatus('connected');
        addLog('[WS] Connected to telemetry multiplexer.');
        ws.send(
          JSON.stringify({
            type: 'trader.telemetry.subscribe',
            accountId: snapshot.accountId,
            lastSequenceId: snapshot.sequenceId,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'trader.telemetry') {
            handleDelta(msg);
          } else if (msg.type === 'trader.snapshot.resync' || msg.type === 'trader.snapshot.response') {
            if (msg.snapshot) {
              setSnapshot(msg.snapshot);
              setConnectionStatus(msg.snapshot.bridgeStatus);
              addLog(`[RESYNC] Sequence reset to #${msg.snapshot.sequenceId}`);
            }
          } else if (msg.type === 'trader.command.result') {
            setRejectedIntentModal(msg);
            addLog(`[COMMAND REJECTED] ${msg.code}: ${msg.message}`);
          }
        } catch {}
      };

      ws.onerror = () => {
        setConnectionStatus('degraded');
      };

      ws.onclose = () => {
        if (!isEmergencyDisconnected) {
          setConnectionStatus('reconnecting');
          addLog('[WS] Disconnected. Reconnecting in 3s...');
        }
      };
    } catch {
      setConnectionStatus('reconnecting');
    }

    return () => {
      if (ws) ws.close();
    };
  }, [isEmergencyDisconnected, snapshot.accountId]);

  const addLog = (entry: string) => {
    setLogs((prev) => [entry, ...prev.slice(0, 49)]);
  };

  const handleDelta = (envelope: any) => {
    const payload = envelope.event;
    if (!payload) return;

    setSnapshot((prev) => {
      let next = { ...prev, sequenceId: envelope.sequenceId, serverTime: envelope.timestamp };

      if (payload.type === 'account') {
        next = { ...next, ...payload.payload };
        const eq = parseFloat(payload.payload.equity);
        setEquityHistory((hist) => [...hist.slice(-29), eq]);
      } else if (payload.type === 'position_upsert') {
        const posList = prev.positions.filter((p) => p.ticket !== payload.payload.ticket);
        next = { ...next, positions: [...posList, payload.payload] };
      } else if (payload.type === 'position_close') {
        const posList = prev.positions.filter((p) => p.ticket !== payload.payload.ticket);
        next = { ...next, positions: posList };
        addLog(`[CLOSE] Ticket #${payload.payload.ticket} at ${payload.payload.closedPrice} PnL=${payload.payload.realizedPnL}`);
      } else if (payload.type === 'bridge_health') {
        setConnectionStatus(payload.payload.status);
        if (payload.payload.status === 'stale') {
          addLog(`[HEARTBEAT] Bridge stale (ping ${payload.payload.pingMs}ms > 3000ms threshold)`);
        }
      }
      return next;
    });
  };

  // 3. Rolling Sparkline Chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || equityHistory.length < 2) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const min = Math.min(...equityHistory) * 0.999;
    const max = Math.max(...equityHistory) * 1.001;
    const range = max - min || 1;

    ctx.beginPath();
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';

    equityHistory.forEach((val, idx) => {
      const x = (idx / (equityHistory.length - 1)) * w;
      const y = h - ((val - min) / range) * (h - 8) - 4;
      if (idx === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Subtle area gradient fill
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
    grad.addColorStop(1, 'rgba(16, 185, 129, 0.0)');
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
  }, [equityHistory]);

  // 4. Command Intent execution trigger (Strictly Gated Rejection)
  const handleCommandIntent = (action: 'BUY' | 'SELL', symbol: string, volume: DecimalString) => {
    const intent = {
      intentId: `intent-${Date.now()}`,
      accountId: snapshot.accountId,
      action,
      symbol,
      volume,
      timestamp: new Date().toISOString(),
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'trader.command.intent', intent }));
    } else {
      fetch('/api/v1/trader/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(intent),
      })
        .then((res) => res.json())
        .then((data) => {
          setRejectedIntentModal(data);
          addLog(`[COMMAND REJECTED] ${data.code}: ${data.message || data.reason}`);
        });
    }
  };

  // 5. Emergency Disconnect Switch
  const handleEmergencyDisconnect = () => {
    setIsEmergencyDisconnected(true);
    setConnectionStatus('disconnected');
    if (wsRef.current) {
      wsRef.current.close(4000, 'EMERGENCY_DISCONNECT');
    }
    setSnapshot((prev) => ({
      ...prev,
      bridgeStatus: 'disconnected',
      positions: [],
    }));
    addLog('[HALT] Emergency Disconnect engaged. Socket severed & local state cleared.');
  };

  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-emerald-950/80 text-emerald-400 border border-emerald-800/80">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </span>
        );
      case 'reconnecting':
        return (
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-amber-950/80 text-amber-400 border border-amber-800/80">
            <RefreshCw className="w-2.5 h-2.5 animate-spin" /> RECONNECTING
          </span>
        );
      case 'stale':
        return (
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-orange-950/80 text-orange-400 border border-orange-800/80 animate-pulse">
            <AlertTriangle className="w-2.5 h-2.5" /> STALE FEED
          </span>
        );
      case 'disconnected':
      default:
        return (
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-rose-950/80 text-rose-400 border border-rose-800/80">
            <PowerOff className="w-2.5 h-2.5" /> DISCONNECTED
          </span>
        );
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #0f172a))',
        borderColor: 'var(--theme-border-highlight, rgba(16,185,129,0.3))',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-4xl backdrop-blur-2xl animate-fadeIn font-sans flex flex-col gap-5"
    >
      {/* Header telemetry ribbon */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-800/80">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-950/40 border border-emerald-800/40 text-emerald-400">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-white tracking-wide">{entry.label || 'MT5 Sovereign Bridge'}</h2>
              {getStatusBadge()}
            </div>
            <div className="text-xs text-slate-400 font-mono mt-0.5 flex items-center gap-2">
              <span>{snapshot.broker}</span>
              <span>•</span>
              <span className="text-emerald-400">{snapshot.accountId}</span>
              <span>•</span>
              <span className="text-slate-500">Seq #{snapshot.sequenceId}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleEmergencyDisconnect}
            disabled={isEmergencyDisconnected}
            className="px-3 py-1.5 rounded-lg text-xs font-bold font-mono bg-rose-950/60 text-rose-300 border border-rose-800/60 hover:bg-rose-900/60 disabled:opacity-40 transition-colors flex items-center gap-1.5"
            title="Emergency Disconnect & State Sever"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> KILL SWITCH
          </button>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Account Balances Grid + Rolling Equity Canvas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono">
          <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Balance</span>
            <span className="text-sm font-bold text-slate-100">${snapshot.balance}</span>
          </div>
          <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Equity</span>
            <span className="text-sm font-bold text-emerald-400">${snapshot.equity}</span>
          </div>
          <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Margin Used</span>
            <span className="text-sm font-bold text-amber-300">${snapshot.marginUsed}</span>
          </div>
          <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Free Margin</span>
            <span className="text-sm font-bold text-slate-100">${snapshot.freeMargin}</span>
          </div>
        </div>

        {/* Equity Sparkline Canvas */}
        <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800 flex flex-col justify-between">
          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-1">
            <span>ROLLING EQUITY</span>
            <span className="text-emerald-400 font-bold">{equityHistory[equityHistory.length - 1]?.toFixed(2)}</span>
          </div>
          <canvas ref={canvasRef} width={220} height={50} className="w-full h-12" />
        </div>
      </div>

      {/* Open Positions Table */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-300 px-1">
          <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-emerald-400" /> Open Positions ({snapshot.positions.length})</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleCommandIntent('BUY', 'EURUSD', '0.10')}
              className="px-2.5 py-1 text-[11px] font-bold rounded bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
            >
              + Quick BUY EURUSD
            </button>
            <button
              onClick={() => handleCommandIntent('SELL', 'EURUSD', '0.10')}
              className="px-2.5 py-1 text-[11px] font-bold rounded bg-rose-600 hover:bg-rose-500 text-white transition-colors"
            >
              - Quick SELL EURUSD
            </button>
          </div>
        </div>

        <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/50">
          <table className="w-full text-left border-collapse text-xs font-mono">
            <thead>
              <tr className="bg-slate-950/80 border-b border-slate-800 text-[10px] text-slate-400 uppercase">
                <th className="py-2.5 px-3">Ticket</th>
                <th className="py-2.5 px-3">Symbol</th>
                <th className="py-2.5 px-3">Side</th>
                <th className="py-2.5 px-3">Lots</th>
                <th className="py-2.5 px-3">Open Price</th>
                <th className="py-2.5 px-3">Mark Price</th>
                <th className="py-2.5 px-3 text-right">Floating P/L</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {snapshot.positions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-500 font-sans">
                    No active positions in current snapshot.
                  </td>
                </tr>
              ) : (
                snapshot.positions.map((pos) => {
                  const pnl = parseFloat(pos.floatingPnL);
                  const isPositive = pnl >= 0;
                  return (
                    <tr key={pos.ticket} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-2.5 px-3 text-slate-400">{pos.ticket}</td>
                      <td className="py-2.5 px-3 font-bold text-white">{pos.symbol}</td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            pos.side === 'BUY'
                              ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/60'
                              : 'bg-rose-950 text-rose-400 border border-rose-800/60'
                          }`}
                        >
                          {pos.side === 'BUY' ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                          {pos.side}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-slate-200">{pos.volume}</td>
                      <td className="py-2.5 px-3 text-slate-300">{pos.openPrice}</td>
                      <td className="py-2.5 px-3 text-slate-200">{pos.currentPrice}</td>
                      <td className={`py-2.5 px-3 text-right font-bold ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {isPositive ? `+$${pos.floatingPnL}` : `-$${Math.abs(pnl).toFixed(2)}`}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log Feed */}
      <div className="space-y-1">
        <span className="text-[10px] uppercase font-mono tracking-wider text-slate-400 px-1 block">Live Bridge Telemetry Stream</span>
        <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-2.5 font-mono text-[11px] h-24 overflow-y-auto space-y-1">
          {logs.map((log, i) => (
            <div key={i} className="text-slate-400 truncate hover:text-slate-200">
              {log}
            </div>
          ))}
        </div>
      </div>

      {/* Typed Rejection Modal Dialog */}
      {rejectedIntentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-rose-800/80 rounded-2xl p-5 max-w-sm w-full shadow-2xl space-y-3 font-sans">
            <div className="flex items-center gap-2.5 text-rose-400">
              <ShieldAlert className="w-6 h-6 flex-shrink-0" />
              <h3 className="font-bold text-sm">Execution Gated: GUEST/READONLY</h3>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              {rejectedIntentModal.reason || (rejectedIntentModal as any).message}
            </p>
            <div className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 font-mono text-[10px] text-slate-400 space-y-1">
              <div>CODE: <span className="text-rose-400">{rejectedIntentModal.code}</span></div>
              <div>INTENT: <span className="text-slate-300">{rejectedIntentModal.intentId}</span></div>
              <div>STATUS: <span className="text-rose-400">{rejectedIntentModal.status}</span></div>
            </div>
            <button
              onClick={() => setRejectedIntentModal(null)}
              className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold transition-colors"
            >
              Acknowledge & Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
